/**
 * Offline image byte cache.
 *
 * Stored image URLs (Supabase Storage) need the network twice: once to sign the
 * URL and once to fetch the bytes. That makes them blank offline. This module
 * keeps the actual image bytes in IndexedDB (not localStorage — blobs would blow
 * its ~5MB quota) keyed by a stable `bucket/path` string, so a previously seen
 * image can be re-served offline as a `blob:` object URL.
 *
 * The cache is bounded: it enforces a total-size budget and evicts the
 * least-recently-used images when the budget is exceeded, so it can't grow
 * without limit as images from deleted quotes accumulate.
 *
 * Everything degrades gracefully: with no IndexedDB (SSR/jsdom) the reads miss
 * and the writes no-op, so callers simply fall back to their normal placeholder.
 */

const DB_NAME = 'orcamigo-media-cache';
const IMAGES_STORE = 'images'; // key -> Blob
const META_STORE = 'meta'; // key -> { size, at }
const DB_VERSION = 1;

// Total-size budget. When exceeded, evict oldest-accessed images down to the
// low-water mark (evicting to a lower bound avoids thrashing on every write).
const DEFAULT_MAX_BYTES = 80 * 1024 * 1024; // 80 MB
const DEFAULT_LOW_WATER_BYTES = 60 * 1024 * 1024; // 60 MB
// Don't rewrite an entry's access time more than once per this window, so
// repeated reads of the same image don't cause a write storm.
const TOUCH_THROTTLE_MS = 60 * 1000;

let maxBytes = DEFAULT_MAX_BYTES;
let lowWaterBytes = DEFAULT_LOW_WATER_BYTES;

export interface StoredMeta {
  key: string;
  size: number;
  at: number;
}

export interface ImageBackend {
  getBlob(key: string): Promise<Blob | null>;
  putImage(key: string, blob: Blob, size: number, at: number): Promise<void>;
  touch(key: string, at: number): Promise<void>;
  has(key: string): Promise<boolean>;
  delete(key: string): Promise<void>;
  list(): Promise<StoredMeta[]>;
  clear(): Promise<void>;
}

function idbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;

function getDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IMAGES_STORE)) db.createObjectStore(IMAGES_STORE);
        if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }).catch((e) => {
      // Reset so a later call can retry a transient open failure.
      dbPromise = null;
      throw e;
    });
  }
  return dbPromise;
}

const idbBackend: ImageBackend = {
  async getBlob(key) {
    const db = await getDb();
    return new Promise<Blob | null>((resolve, reject) => {
      const tx = db.transaction(IMAGES_STORE, 'readonly');
      const req = tx.objectStore(IMAGES_STORE).get(key);
      req.onsuccess = () => resolve((req.result as Blob) ?? null);
      req.onerror = () => reject(req.error);
    });
  },
  async putImage(key, blob, size, at) {
    const db = await getDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction([IMAGES_STORE, META_STORE], 'readwrite');
      tx.objectStore(IMAGES_STORE).put(blob, key);
      tx.objectStore(META_STORE).put({ size, at }, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  },
  async touch(key, at) {
    const db = await getDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(META_STORE, 'readwrite');
      const store = tx.objectStore(META_STORE);
      const req = store.get(key);
      req.onsuccess = () => {
        const meta = req.result as { size: number; at: number } | undefined;
        if (meta) store.put({ size: meta.size, at }, key);
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  },
  async has(key) {
    const db = await getDb();
    return new Promise<boolean>((resolve, reject) => {
      const tx = db.transaction(META_STORE, 'readonly');
      const req = tx.objectStore(META_STORE).getKey(key);
      req.onsuccess = () => resolve(req.result !== undefined);
      req.onerror = () => reject(req.error);
    });
  },
  async delete(key) {
    const db = await getDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction([IMAGES_STORE, META_STORE], 'readwrite');
      tx.objectStore(IMAGES_STORE).delete(key);
      tx.objectStore(META_STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  },
  async list() {
    const db = await getDb();
    return new Promise<StoredMeta[]>((resolve, reject) => {
      const out: StoredMeta[] = [];
      const tx = db.transaction(META_STORE, 'readonly');
      const req = tx.objectStore(META_STORE).openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          const v = cursor.value as { size: number; at: number };
          out.push({ key: String(cursor.key), size: v.size, at: v.at });
          cursor.continue();
        } else {
          resolve(out);
        }
      };
      req.onerror = () => reject(req.error);
    });
  },
  async clear() {
    const db = await getDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction([IMAGES_STORE, META_STORE], 'readwrite');
      tx.objectStore(IMAGES_STORE).clear();
      tx.objectStore(META_STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  },
};

const noopBackend: ImageBackend = {
  getBlob: async () => null,
  putImage: async () => {},
  touch: async () => {},
  has: async () => false,
  delete: async () => {},
  list: async () => [],
  clear: async () => {},
};

let backend: ImageBackend = idbAvailable() ? idbBackend : noopBackend;

let nowFn: () => number = () => Date.now();

// Best-effort in-memory running total of cached bytes. `null` until the first
// authoritative scan; kept in sync on writes/evictions so we only re-scan when
// the budget is actually crossed.
let runningBytes: number | null = null;
// Per-session throttle for access-time rewrites.
const lastTouched = new Map<string, number>();

// Object URLs are expensive to mint and leak if not revoked, so memoize one per
// key for the page's lifetime (bounded by the number of distinct images).
const objectUrls = new Map<string, string>();

function revokeObjectUrl(key: string): void {
  const u = objectUrls.get(key);
  if (!u) return;
  objectUrls.delete(key);
  if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    try {
      URL.revokeObjectURL(u);
    } catch {
      // ignore
    }
  }
}

function revokeAllObjectUrls(): void {
  for (const key of Array.from(objectUrls.keys())) revokeObjectUrl(key);
}

async function ensureInit(): Promise<void> {
  if (runningBytes !== null) return;
  try {
    const items = await backend.list();
    runningBytes = items.reduce((sum, it) => sum + (it.size || 0), 0);
  } catch {
    runningBytes = 0;
  }
}

/**
 * Evicts least-recently-accessed images until total bytes fall to the low-water
 * mark. Runs only when a write pushes the running total past the budget.
 */
async function enforceBudget(): Promise<void> {
  try {
    const items = await backend.list();
    let total = items.reduce((sum, it) => sum + (it.size || 0), 0);
    if (total <= maxBytes) {
      runningBytes = total;
      return;
    }
    items.sort((a, b) => a.at - b.at); // oldest access first
    for (const it of items) {
      if (total <= lowWaterBytes) break;
      await backend.delete(it.key);
      revokeObjectUrl(it.key);
      lastTouched.delete(it.key);
      total -= it.size || 0;
    }
    runningBytes = total;
  } catch {
    // Best-effort — leave the cache as-is; the next write retries.
  }
}

export async function hasCachedImage(key: string): Promise<boolean> {
  try {
    return await backend.has(key);
  } catch {
    return false;
  }
}

export async function putCachedImage(key: string, blob: Blob): Promise<void> {
  try {
    await ensureInit();
    const size = blob.size || 0;
    await backend.putImage(key, blob, size, nowFn());
    lastTouched.set(key, nowFn());
    runningBytes = (runningBytes ?? 0) + size;
    if (runningBytes > maxBytes) await enforceBudget();
  } catch {
    // Best-effort: a full/again-unavailable store just means no offline copy.
  }
}

function maybeTouch(key: string): void {
  const t = nowFn();
  const prev = lastTouched.get(key) ?? 0;
  if (t - prev < TOUCH_THROTTLE_MS) return;
  lastTouched.set(key, t);
  void backend.touch(key, t).catch(() => {});
}

export async function getCachedImageBlob(key: string): Promise<Blob | null> {
  try {
    const blob = await backend.getBlob(key);
    if (blob) maybeTouch(key);
    return blob;
  } catch {
    return null;
  }
}

/**
 * Returns a `blob:` object URL for a cached image, or null if it isn't cached
 * (or object URLs aren't supported here). The URL is stable across calls.
 */
export async function getCachedImageObjectUrl(key: string): Promise<string | null> {
  const existing = objectUrls.get(key);
  if (existing) {
    maybeTouch(key);
    return existing;
  }

  const blob = await getCachedImageBlob(key);
  if (!blob) return null;

  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return null;
  }
  const objectUrl = URL.createObjectURL(blob);
  objectUrls.set(key, objectUrl);
  return objectUrl;
}

/**
 * Downloads `fetchableUrl` (a signed/public URL that currently resolves) and
 * stores its bytes under `key`. No-ops if already cached. Returns whether the
 * cache now holds the image. Never throws.
 */
export async function cacheImageBytes(key: string, fetchableUrl: string): Promise<boolean> {
  try {
    if (await hasCachedImage(key)) return true;
    const res = await fetch(fetchableUrl);
    if (!res.ok) return false;
    const blob = await res.blob();
    if (!blob || blob.size === 0) return false;
    await putCachedImage(key, blob);
    return true;
  } catch {
    return false;
  }
}

/**
 * Wipes every cached image byte and forgets memoized object URLs. Call on logout
 * / "clear all data" so one user's images never persist for the next account on
 * a shared device (localStorage caches are cleared separately).
 */
export async function clearImageCache(): Promise<void> {
  revokeAllObjectUrls();
  lastTouched.clear();
  runningBytes = 0;
  try {
    await backend.clear();
  } catch {
    // Best-effort — a failure here just leaves stale bytes that are overwritten
    // (immutable paths) or evicted under storage pressure.
  }
}

/** Swap the storage backend. Test-only — lets specs run without a real IndexedDB. */
export function __setImageBackend(next: ImageBackend | null): void {
  backend = next ?? (idbAvailable() ? idbBackend : noopBackend);
  runningBytes = null;
  lastTouched.clear();
}

/** Test-only: control the clock used for access times / eviction ordering. */
export function __setNowForTests(fn: (() => number) | null): void {
  nowFn = fn ?? (() => Date.now());
}

/** Test-only: shrink the size budget so eviction can be exercised deterministically. */
export function __setBudgetForTests(max: number | null, lowWater: number | null): void {
  maxBytes = max ?? DEFAULT_MAX_BYTES;
  lowWaterBytes = lowWater ?? DEFAULT_LOW_WATER_BYTES;
}

/** Test helper: forget memoized object URLs (does not touch stored bytes). */
export function __resetObjectUrlCache(): void {
  revokeAllObjectUrls();
}
